
import { computed, defineComponent, ref, watch } from 'vue';
import {
	required,
	requiredIf
} from '@vuelidate/validators';
import {
	UiInput,
	SelectOptions,
	UiSelect,
	UiFormError,
	SvgIcon,
	useForm
} from '@/ui-kit';
import { Nullable, uuid } from '@/utils';
import PhoneMenuItemDialog from './PhoneMenuItemDialog.vue';
import { usePhoneNumbers } from '../composition/use-phone-numbers';

export default defineComponent({
	name: 'phone-menu-item',
	components: {
		UiInput,
		UiSelect,
		SvgIcon,
		UiFormError,
		PhoneMenuItemDialog
	},
	emits: ['update:modelValue', 'removeItem'],
	props: {
		modelValue: { type: Object, required: true },
		otherItems: { type: Array, required: true },
		disabled: { type: Boolean, default: false }
	},
	setup(props, { emit }) {
		const { phoneNumbers } = usePhoneNumbers();
		const dialog =
			ref<Nullable<typeof PhoneMenuItemDialog>>(null);
		const { formData, fillFormData, ...restForm } =
			useForm(
				{
					id: props.modelValue.id || uuid(5),
					key: props.modelValue.key || '',
					voice: props.modelValue.voice || '',
					destination:
						props.modelValue.destination || '',
					type: props.modelValue.type || 3
				},
				computed(() => ({
					key: {
						required
					},
					voice: {
						required
					},
					destination: {
						required: requiredIf(
							(): boolean =>
								formData.type === 1
						)
					}
				}))
			);

		const selectedPhone = computed(() => {
			return phoneNumbers.value.find(
				(number) =>
					number.phoneNumber.trim() ===
					formData.destination?.replace(
						/[ -]/g,
						''
					)
			);
		});

		const options = computed<SelectOptions>(() => {
			const items = props.otherItems.map(
				(item: any) => item.key
			);
			return [
				{
					value: 1,
					title: '1'
				},
				{
					value: 2,
					title: '2'
				},
				{
					value: 3,
					title: '3'
				},
				{
					value: 4,
					title: '4'
				},
				{
					value: 5,
					title: '5'
				},
				{
					value: 6,
					title: '6'
				},
				{
					value: 7,
					title: '7'
				},
				{
					value: 8,
					title: '8'
				},
				{
					value: 9,
					title: '9'
				},
				{
					value: 0,
					title: '0'
				}
			].filter(
				(item) =>
					formData.key ===
						item.value.toString() ||
					!items.includes(item.value.toString())
			);
		});

		watch(
			() => formData,
			() => emit('update:modelValue', formData),
			{ deep: true }
		);

		const edit = () => {
			dialog.value?.openDialog(formData);
		};

		const onUpdate = (
			data: Partial<typeof formData>
		) => {
			fillFormData(data);
			emit('update:modelValue', formData);
		};

		return {
			onUpdate,
			edit,
			options,
			dialog,
			formData,
			selectedPhone,
			...restForm
		};
	}
});
